/**
 * Converts Contentful's "Section" content type to the appropriate component
 *
 * Copyright (c) 2020 Coursera
 * Author: Kelvin Nguyen
 */
import React from 'react'
import PropTypes from 'prop-types'
import { BasicSection, TwoColumnSection } from 'components/CTASections'
import { SimpleHero, SubHero } from 'components/Hero'
import ContentBlock from '../ContentBlock'
import ContentfulModalButton from '../ContentfulModalButton'
import ContentfulGridContainer from '../ContentfulGridContainer'
import { convertRichTextToComponent } from '../utils'

const ContentfulSection = ({
  type,
  title,
  subtitle,
  tagline,
  footer,
  backgroundColor,
  backgroundImage,
  backgroundImageMobile,
  backgroundVideo,
  flipped,
  buttons,
  titleSettings,
  contentBlocks,
  ...props
}) => {
  const updatedContentBlocks = contentBlocks || []

  // Render all the inner content blocks for this section
  const ContentBlocks = updatedContentBlocks.map((content) => {
    if (!content) return null

    const contentType = content.__typename
    const key = content.id

    return (contentType === 'ContentfulGridContainer') ? (
      <ContentfulGridContainer key={key} {...content} />
    ) : (
      <ContentBlock key={key} content={content} />
    )
  })

  const updatedButtons = (buttons || []).map((button) => {
    const buttonType = button?.__typename

    return (buttonType === 'ContentfulModalButton') ? ({
      ...button,
      modalContent: (<ContentfulModalButton contentBlocks={button?.content} />),
    }) : button
  })

  const sectionProps = {
    title: convertRichTextToComponent(title),
    // Since subtitles can contain super rich text, we leave the fomratting as is
    subtitle: convertRichTextToComponent(subtitle, true),
    tagline: convertRichTextToComponent(tagline),
    footer: convertRichTextToComponent(footer),
    backgroundColor,
    backgroundImage: backgroundImage?.file?.url || null,
    backgroundImageMobile: backgroundImageMobile?.file?.url || null,
    backgroundVideo: backgroundVideo?.file?.url || null,
    buttons: updatedButtons,
    flipped: flipped || false,
    titleSettings: titleSettings || {},
    ...props,
  }

  switch (type) {
    case 'simpleHero':
      return (
        <SimpleHero {...sectionProps}>
          {ContentBlocks}
        </SimpleHero>
      )
    case 'subHero':
      return (
        <SubHero {...sectionProps}>
          {ContentBlocks}
        </SubHero>
      )
    case 'twoColumn':
      return (
        <TwoColumnSection {...sectionProps}>
          {ContentBlocks}
        </TwoColumnSection>
      )
    case 'basic':
    default:
      return (
        <BasicSection {...sectionProps}>
          {ContentBlocks}
        </BasicSection>
      )
  }
}

ContentfulSection.propTypes = {
  type: PropTypes.oneOf(['basic', 'twoColumn', 'simpleHero', 'mainHero', 'subHero']),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  tagline: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  footer: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  backgroundColor: PropTypes.string,
  backgroundImage: PropTypes.object,
  backgroundImageMobile: PropTypes.object,
  backgroundVideo: PropTypes.object,
  flipped: PropTypes.bool,
  buttons: PropTypes.array,
  titleSettings: PropTypes.object,
  contentBlocks: PropTypes.array,
}

export default ContentfulSection
